import React, { useEffect, useMemo, useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Calculator, FileDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { opMeta } from "../lib/opTypes";

function formatBRL(n) {
  return `R$ ${(Number(n) || 0).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function groupByHolder(banks) {
  const groups = {};
  for (const b of banks) {
    const key = (b.account_holder && b.account_holder.trim()) || "Sem titular";
    (groups[key] ||= []).push(b);
  }
  const keys = Object.keys(groups).sort((a, b) => {
    if (a === "Sem titular") return 1;
    if (b === "Sem titular") return -1;
    return a.localeCompare(b, "pt-BR");
  });
  return keys.map((k) => [k, groups[k]]);
}

const nowBR = () =>
  new Date().toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo", dateStyle: "short", timeStyle: "short" });

export default function BankCheckDialog({ open, onOpenChange, banks }) {
  const [values, setValues] = useState({});
  const [holderFilter, setHolderFilter] = useState("all");
  const [openEntries, setOpenEntries] = useState([]);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (!open) return;
    setHolderFilter("all");
    api
      .get("/entries", { params: { finalized: false } })
      .then((r) => setOpenEntries(r.data || []))
      .catch(() => setOpenEntries([]));
  }, [open]);

  const groups = useMemo(() => groupByHolder(banks), [banks]);
  const holders = groups.map(([h]) => h);
  const visibleGroups = holderFilter === "all" ? groups : groups.filter(([h]) => h === holderFilter);

  const num = (id) => parseFloat(String(values[id] ?? "").replace(",", ".")) || 0;
  const isFilled = (id) => values[id] !== undefined && values[id] !== "";
  const total = banks.reduce((s, b) => s + num(b.id), 0);
  const filled = banks.filter((b) => isFilled(b.id)).length;
  const openRevenue = openEntries.reduce((s, e) => s + (e.revenue || 0), 0);

  const generatePdf = () => {
    setGenerating(true);
    try {
      const doc = new jsPDF();
      const when = nowBR();
      doc.setFont("helvetica", "bold");
      doc.setFontSize(18);
      doc.text("Sure FPS — Conferência de bancas", 14, 18);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(110);
      doc.text(`Gerado em ${when} (horário de Brasília)`, 14, 25);
      doc.setTextColor(0);

      let y = 33;
      for (const [holder, list] of groups) {
        const sub = list.reduce((s, b) => s + num(b.id), 0);
        autoTable(doc, {
          startY: y,
          head: [[{ content: holder.toUpperCase(), colSpan: 2, styles: { fillColor: [212, 255, 0], textColor: 0, fontStyle: "bold" } }]],
          body: [
            ...list.map((b) => [b.name, isFilled(b.id) ? formatBRL(num(b.id)) : "— não informado"]),
            [{ content: "Subtotal", styles: { fontStyle: "bold" } }, { content: formatBRL(sub), styles: { fontStyle: "bold" } }],
          ],
          columnStyles: { 1: { halign: "right", cellWidth: 50 } },
          styles: { fontSize: 10, cellPadding: 2.5 },
          theme: "grid",
          margin: { left: 14, right: 14 },
        });
        y = doc.lastAutoTable.finalY + 6;
      }

      autoTable(doc, {
        startY: y,
        body: [
          [{ content: "Total declarado nas bancas", styles: { fontStyle: "bold" } }, { content: formatBRL(total), styles: { fontStyle: "bold", halign: "right" } }],
          [{ content: `Receita em aberto (${openEntries.length} operação${openEntries.length === 1 ? "" : "ões"} em aberto)`, styles: { fontStyle: "bold" } }, { content: formatBRL(openRevenue), styles: { fontStyle: "bold", halign: "right" } }],
          [{ content: "Total geral (bancas + receita em aberto)", styles: { fontStyle: "bold", fillColor: [230, 255, 150] } }, { content: formatBRL(total + openRevenue), styles: { fontStyle: "bold", halign: "right", fillColor: [230, 255, 150] } }],
        ],
        columnStyles: { 1: { cellWidth: 50 } },
        styles: { fontSize: 11, cellPadding: 3 },
        theme: "grid",
        margin: { left: 14, right: 14 },
      });
      y = doc.lastAutoTable.finalY + 8;

      if (openEntries.length > 0) {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(12);
        doc.text("Operações em aberto", 14, y);
        autoTable(doc, {
          startY: y + 3,
          head: [["Data", "Tipo", "Confronto / Casa", "Receita"]],
          body: openEntries.map((e) => {
            const [yy, mm, dd] = (e.date || "").split("-");
            const main = e.bookmakers?.find((b) => b.is_main)?.name || e.bookmakers?.[0]?.name || "";
            const match = e.home_team && e.away_team ? `${e.home_team} x ${e.away_team}` : main;
            return [dd ? `${dd}/${mm}/${yy}` : "", opMeta(e.operation_type).short, match, formatBRL(e.revenue || 0)];
          }),
          columnStyles: { 3: { halign: "right" } },
          headStyles: { fillColor: [30, 30, 30] },
          styles: { fontSize: 9, cellPadding: 2 },
          theme: "striped",
          margin: { left: 14, right: 14 },
        });
      }

      doc.save(`conferencia-bancas-${new Date().toISOString().slice(0, 16).replace(/[:T]/g, "-")}.pdf`);
      toast.success("PDF gerado");
    } catch (e) {
      toast.error("Erro ao gerar PDF");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="bank-check-dialog"
        className="flex max-h-[92vh] flex-col gap-3 overflow-hidden border-white/10 bg-[#121212] p-0 sm:max-w-xl"
      >
        <DialogHeader className="px-6 pt-6">
          <DialogTitle className="flex items-center gap-2 font-display text-xl font-bold text-white">
            <Calculator className="h-5 w-5 text-[#D4FF00]" />
            Conferência de bancas
          </DialogTitle>
          <DialogDescription className="text-sm text-zinc-500">
            Digite o saldo atual de cada banca (ou marque Zero). Os valores não são salvos — gere o PDF para guardar.
          </DialogDescription>
        </DialogHeader>

        {holders.length > 1 && (
          <div className="flex flex-wrap gap-1.5 px-6 pb-1">
            {[["all", "Todos"], ...holders.map((h) => [h, h])].map(([v, label]) => (
              <button
                key={v}
                data-testid={`check-holder-filter-${v}`}
                onClick={() => setHolderFilter(v)}
                className={`rounded-full border px-3 py-1 text-xs font-semibold ${
                  holderFilter === v
                    ? "border-[#D4FF00] bg-[#D4FF00] text-black"
                    : "border-white/10 text-zinc-300 hover:border-white/30 hover:text-white"
                }`}
                style={{ transition: "background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease" }}
              >
                {label}
              </button>
            ))}
          </div>
        )}

        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-6 py-2">
          {visibleGroups.map(([holder, list]) => {
            const sub = list.reduce((s, b) => s + num(b.id), 0);
            return (
              <div key={holder} data-testid={`check-holder-${holder}`}>
                <div className="mb-2 flex items-center justify-between gap-3 rounded-lg border border-[#D4FF00]/25 bg-[#D4FF00]/10 px-3 py-2">
                  <div className="flex items-center gap-2">
                    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#D4FF00] text-xs font-bold text-black">
                      {holder.slice(0, 1).toUpperCase()}
                    </span>
                    <span className="font-display text-base font-bold text-white md:text-lg">{holder}</span>
                    <span className="text-[0.7rem] text-zinc-400">
                      {list.length} banca{list.length > 1 ? "s" : ""}
                    </span>
                  </div>
                  <span className="font-mono-num text-sm font-bold text-[#D4FF00]">{formatBRL(sub)}</span>
                </div>
                <div className="space-y-2">
                  {list.map((b) => {
                    const zero = values[b.id] === "0";
                    return (
                      <div key={b.id} className="flex items-center gap-2">
                        <span className="min-w-0 flex-1 truncate text-sm text-white">{b.name}</span>
                        <button
                          type="button"
                          data-testid={`check-zero-${b.id}`}
                          onClick={() => setValues((v) => ({ ...v, [b.id]: zero ? "" : "0" }))}
                          className={`h-9 shrink-0 rounded-md border px-2.5 text-[0.7rem] font-bold uppercase tracking-wider ${
                            zero
                              ? "border-red-500/50 bg-red-500/20 text-red-300"
                              : "border-white/10 text-zinc-400 hover:border-white/30 hover:text-white"
                          }`}
                          style={{ transition: "background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease" }}
                          title="Conta sem saldo"
                        >
                          Zero
                        </button>
                        <Input
                          data-testid={`check-input-${b.id}`}
                          type="number"
                          step="0.01"
                          inputMode="decimal"
                          placeholder="0,00"
                          value={values[b.id] ?? ""}
                          onChange={(e) => setValues((v) => ({ ...v, [b.id]: e.target.value }))}
                          className={`h-9 w-32 bg-black/40 text-right font-mono-num ${zero ? "text-red-300" : ""}`}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        <DialogFooter className="flex-col gap-3 border-t border-white/10 bg-black/30 px-6 py-4 sm:flex-col sm:items-stretch">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <div className="text-[0.7rem] uppercase tracking-widest text-zinc-500">
                Total · {filled}/{banks.length} bancas
              </div>
              <div data-testid="bank-check-total" className="font-mono-num text-2xl font-bold text-[#D4FF00]">
                {formatBRL(total)}
              </div>
            </div>
            <div className="text-right">
              <div className="text-[0.7rem] uppercase tracking-widest text-zinc-500">
                Receita em aberto · {openEntries.length} op.
              </div>
              <div data-testid="bank-check-open-revenue" className="font-mono-num text-lg font-bold text-[#22C55E]">
                {formatBRL(openRevenue)}
              </div>
              <div className="text-[0.7rem] text-zinc-500">
                Geral <span className="font-mono-num font-semibold text-white">{formatBRL(total + openRevenue)}</span>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            <Button
              data-testid="bank-check-clear"
              variant="ghost"
              onClick={() => setValues({})}
              className="text-zinc-400 hover:text-white"
            >
              Limpar
            </Button>
            <Button
              data-testid="bank-check-pdf"
              onClick={generatePdf}
              disabled={generating || banks.length === 0}
              variant="outline"
              className="gap-2 border-white/15 bg-transparent text-zinc-100 hover:bg-white/5 hover:text-white"
            >
              {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4 text-[#D4FF00]" />}
              Gerar PDF
            </Button>
            <Button
              data-testid="bank-check-close"
              onClick={() => onOpenChange(false)}
              className="bg-[#D4FF00] font-bold text-black hover:bg-[#c4ee00]"
            >
              Fechar
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
